import axios from "axios";
import { BASE_URL } from "../Global";
import useAuthStore from "../Store/AuthStore";

//Send Feedback API
export const sendRatingFeedbackAPI = async (id, cookie, type, feedback) => {
    try {
        const response = await axios.post(`${BASE_URL}/api/wpw/feedback`, {
            rating: id,
            userCookie: cookie,
            type: type,
            feedback,
        });
        return response;
    } catch (error) {
        return error;
    }
};

// Favorite Product API
export const favoriteProductAPI = async (email, giftID, text) => {
    const { token } = useAuthStore.getState();
    try {
        const response = await axios.post(
            `${BASE_URL}/api/user/favorite`,
            {
                email,
                giftID,
                text,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { success: true, message: response.data.message };
    } catch (error) {
        return { success: false, message: error.response.data.error };
    }
};

// Fetch Profile API
export const fetchProfileAPI = async () => {
    const { token, email } = useAuthStore.getState();
    try {
        const response = await axios.post(
            `${BASE_URL}/api/user/profile`,
            {
                email,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        return error;
    }
};

// Update Profile API
export const updateProfileAPI = async (profileData) => {
    const { token, email } = useAuthStore.getState();
    try {
        const response = await axios.post(
            `${BASE_URL}/api/user/update-profile`,

            {
                email,
                ...profileData,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        return error;
    }
};

/**
 * Generate Persona API
 * Works for both authenticated and unauthenticated users
 * Note: This API generates a persona suggestion based on search text.
 * It does NOT create a persona in the database - that requires authentication and createPersonaAPI.
 * For unauthenticated users, this provides a preview/suggestion that can be saved after login.
 *
 * @param {string} usersearch - The search text to generate persona from
 * @returns {Promise<Object>} Response with persona data
 */
export const generatePersonaAPI = async (usersearch) => {
    const { token } = useAuthStore.getState();

    const headers = {
        "Content-Type": "application/json",
    };

    // // Add authorization header if user is authenticated
    // if (token) {
    //   headers.Authorization = `Bearer ${token}`;
    // }

    try {
        const response = await axios.post(
            `${BASE_URL}/api/user/generatepersona`,
            { usersearch }
            // { headers }
        );
        return response.data;
    } catch (error) {
        if (error.response) {
            return {
                success: false,
                message: error.response.data.error || "Failed to generate persona",
            };
        } else {
            return {
                success: false,
                message: "Network error - failed to connect to server",
            };
        }
    }
};

// Create Persona API
export const createPersonaAPI = async (personaData, usersearch = "", language = "") => {
    const { token, email } = useAuthStore.getState();

    // Check if we have the required auth data
    if (!token || !email) {
        return {
            success: false,
            message: "User not authenticated",
        };
    }

    try {
        const response = await axios.post(
            `${BASE_URL}/api/user/create-persona`,
            { ...personaData, email, usersearch, language },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { success: true, data: response.data };
    } catch (error) {
        if (error.response) {
            return {
                success: false,
                message: error.response.data.error || "Failed to create persona",
            };
        } else {
            return {
                success: false,
                message: "Network error - failed to connect to server",
            };
        }
    }
};

// Fetch All Personas API
export const fetchUserPersonasAPI = async () => {
    const { token, email } = useAuthStore.getState();

    // Check if we have the required auth data
    if (!token || !email) {
        return {
            success: false,
            message: "User not authenticated",
        };
    }

    try {
        const response = await axios.post(
            `${BASE_URL}/api/user/personas`,
            { email },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { success: true, data: response.data };
    } catch (error) {
        if (error.response) {
            return {
                success: false,
                message: error.response.data.error || "Failed to fetch personas",
            };
        } else {
            return {
                success: false,
                message: "Network error - failed to connect to server",
            };
        }
    }
};

// Add Persona Search History API
export const addPersonaHistoryAPI = async (personaId, searchText, language = "") => {
    const { token, email } = useAuthStore.getState();

    // Check if we have the required auth data
    if (!token || !email) {
        return {
            success: false,
            message: "User not authenticated",
        };
    }

    try {
        const response = await axios.post(
            `${BASE_URL}/api/user/add-persona-search-history`,
            {
                email: email.toLowerCase(),
                personaId,
                searchText,
                language
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { success: true, data: response.data };
    } catch (error) {
        if (error.response) {
            return {
                success: false,
                message: error.response.data.error || "Failed to add persona search history",
            };
        } else {
            return {
                success: false,
                message: "Network error - failed to connect to server",
            };
        }
    }
};

// Update Persona Summary API
export const updatePersonaSummaryAPI = async (personaId, summary) => {
    const { token, email } = useAuthStore.getState();

    if (!token || !email) {
        return {
            success: false,
            message: "User not authenticated",
        };
    }

    try {
        const response = await axios.put(
            `${BASE_URL}/api/user/update-persona-summary`,
            {
                email: email.toLowerCase(),
                personaId,
                summary
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { success: true, data: response.data };
    } catch (error) {
        if (error.response) {
            return {
                success: false,
                message: error.response.data.error || "Failed to update persona summary",
            };
        } else {
            return {
                success: false,
                message: "Network error - failed to connect to server",
            };
        }
    }
};

// Delete Persona API
export const deletePersonaAPI = async (personaId) => {
    const { token, email } = useAuthStore.getState();

    if (!token || !email) {
        return {
            success: false,
            message: "User not authenticated",
        };
    }

    try {
        const response = await axios.put(
            `${BASE_URL}/api/user/delete-persona`,
            {
                email: email.toLowerCase(),
                personaId
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { success: true, data: response.data };
    } catch (error) {
        if (error.response) {
            return {
                success: false,
                message: error.response.data.error || "Failed to delete persona",
            };
        } else {
            return {
                success: false,
                message: "Network error - failed to connect to server",
            };
        }
    }
};
// Fetch Cart API
export const fetchCartAPI = async () => {
    const { token } = useAuthStore.getState();
    if (!token) return { success: false, message: "Not authenticated" };

    try {
        const response = await axios.get(`${BASE_URL}/api/user/cart`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, message: error.response?.data?.error || "Failed to fetch cart" };
    }
};

// Update/Sync Cart API
export const updateCartAPI = async (cart) => {
    const { token } = useAuthStore.getState();
    if (!token) return { success: false, message: "Not authenticated" };

    try {
        const response = await axios.post(
            `${BASE_URL}/api/user/cart`,
            { cart },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, message: error.response?.data?.error || "Failed to update cart" };
    }
};

// Add to Cart API
export const addToCartAPI = async (giftId, quantity = 1) => {
    const { token } = useAuthStore.getState();
    if (!token) return { success: false, message: "Not authenticated" };

    try {
        const response = await axios.post(
            `${BASE_URL}/api/user/cart/add`,
            { giftId, quantity },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, message: error.response?.data?.error || "Failed to add to cart" };
    }
};

// Remove from Cart API
export const removeFromCartAPI = async (giftId) => {
    const { token } = useAuthStore.getState();
    if (!token) return { success: false, message: "Not authenticated" };

    try {
        const response = await axios.post(
            `${BASE_URL}/api/user/cart/remove`,
            { giftId },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, message: error.response?.data?.error || "Failed to remove from cart" };
    }
};

// Sync/Merge Cart API
export const syncCartAPI = async (cart) => {
    const { token } = useAuthStore.getState();
    if (!token) return { success: false, message: "Not authenticated" };

    try {
        const response = await axios.post(
            `${BASE_URL}/api/user/cart/sync`,
            { cart },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { success: true, data: response.data };
    } catch (error) {
        return { success: false, message: error.response?.data?.error || "Failed to sync cart" };
    }
};
